import React, { useState, useEffect } from 'react';
import { FiSearch, FiEdit, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

const ModifyFactors = () => {
  const [stats, setStats] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [factorToDelete, setFactorToDelete] = useState(null);
  const [newFactor, setNewFactor] = useState({
    title: '',
    value: '',
    isActive: true,
  });
  const [editedStats, setEditedStats] = useState([]);
  const [isConfirmEditOpen, setIsConfirmEditOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Fetch data logic
  useEffect(() => {
    const fetchFactors = async () => {
      try {
        const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factors`);
        const data = await response.json();
        const formattedData = data
          .map((factor) => ({
            id: factor.factorid,
            title: factor.factorname,
            value: factor.defaultpercentage,
            additional: '',
            isActive: factor.isactive,
          }))
          .sort((a, b) => a.id - b.id);

        setStats(formattedData);
      } catch (error) {
        toast.error('Error fetching factors: ' + error.message);
      }
    };

    fetchFactors();
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredStats = stats.filter((stat) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      stat.title.toLowerCase().includes(searchLower) || stat.value.toString().includes(searchLower)
    );
  });

  const handleDeleteFactor = (factor) => {
    setFactorToDelete(factor);
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!factorToDelete) return;

    try {
      const response = await fetch(
        `${AQ_PRICING_BASE_URL}/pricing/api/delete-factor/${factorToDelete.id}`,
        {
          method: 'DELETE',
          headers: { accept: 'application/json' },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setStats((prevStats) => prevStats.filter((stat) => stat.id !== factorToDelete.id));
        toast.success(`"${factorToDelete.title}" deleted successfully`);
      } else {
        toast.error(`Failed to delete factor: ${data.message}`);
      }
    } catch (error) {
      toast.error('Error with DELETE request: ' + error.message);
    } finally {
      setIsConfirmDeleteOpen(false);
      setFactorToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmDeleteOpen(false);
    setFactorToDelete(null);
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setIsConfirmEditOpen(true);
    } else {
      setIsEditing(true);
      setEditedStats(stats);
    }
  };

  const handleSaveEdit = async () => {
    const updatedFactors = editedStats.map((stat) => ({
      factorid: stat.id,
      factorname: stat.title,
      defaultpercentage: parseFloat(stat.value),
      isactive: stat.isActive,
    }));

    let successCount = 0;
    let errorCount = 0;

    try {
      const updatePromises = updatedFactors.map(async (factor) => {
        const response = await fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/update-factor/${factor.factorid}`,
          {
            method: 'PUT',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(factor),
          }
        );

        const data = await response.json();

        if (response.ok) {
          setStats((prevStats) =>
            prevStats.map((stat) =>
              stat.id === factor.factorid
                ? {
                    ...stat,
                    value: factor.defaultpercentage,
                    title: factor.factorname,
                    isActive: factor.isactive,
                  }
                : stat
            )
          );
          successCount++;
        } else {
          errorCount++;
          toast.error('Error updating factor: ' + data.message);
        }
      });

      await Promise.all(updatePromises);

      if (successCount === updatedFactors.length) {
        toast.success('All factors updated successfully');
      } else {
        toast.error(`${errorCount} factors failed to update`);
      }

      setIsConfirmEditOpen(false);
      setIsEditing(false);
    } catch (error) {
      toast.error('Error with PUT request: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditedStats(stats);
    setIsConfirmEditOpen(false);
    setIsEditing(false);
  };

  const handleValueChange = (id, newValue) => {
    setEditedStats((prevStats) =>
      prevStats.map((stat) => (stat.id === id ? { ...stat, value: newValue } : stat))
    );
  };

  const handleIncrement = (id) => {
    setEditedStats((prevStats) =>
      prevStats.map((stat) =>
        stat.id === id ? { ...stat, value: (parseFloat(stat.value) + 0.01).toFixed(2) } : stat
      )
    );
  };

  const handleDecrement = (id) => {
    setEditedStats((prevStats) =>
      prevStats.map((stat) =>
        stat.id === id ? { ...stat, value: (parseFloat(stat.value) - 0.01).toFixed(2) } : stat
      )
    );
  };

  const handleToggleActive = (id) => {
    if (!isEditing) return;

    setEditedStats((prevStats) =>
      prevStats.map((stat) => (stat.id === id ? { ...stat, isActive: !stat.isActive } : stat))
    );
  };

  const handleNewFactorToggleActive = () => {
    setNewFactor((prev) => ({ ...prev, isActive: !prev.isActive }));
  };

  const handleNewFactorOpen = () => {
    setIsPopupOpen(true);
  };

  const handleNewFactorClose = () => {
    setIsPopupOpen(false);
    setNewFactor({ title: '', value: '', isActive: true });
  };

  const handleNewFactorSave = async () => {
    if (newFactor.title && newFactor.value) {
      const factorData = {
        factorname: newFactor.title,
        defaultpercentage: parseFloat(newFactor.value),
        isactive: newFactor.isActive,
      };

      try {
        const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/create-factor`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(factorData),
        });

        const data = await response.json();

        if (response.ok) {
          setStats((prevStats) => [
            ...prevStats,
            {
              id: data.factor.factorid,
              title: data.factor.factorname,
              value: data.factor.defaultpercentage,
              additional: '',
              isActive: data.factor.isactive,
            },
          ]);
          handleNewFactorClose();
          toast.success('New factor created successfully!');
        } else {
          toast.error('Error creating factor: ' + data.message);
        }
      } catch (error) {
        toast.error('Error with fetch: ' + error.message);
      }
    }
  };

  return (
    <div className="bg-gray-100 h-full p-6">
      {/* Keep your original header styling */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
            className="btn"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
            className="btn"
          >
            Templates
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
            className="bt text-black font-bold"
          >
            Factors
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
            className="btn"
          >
            Pricing
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
            className="btn"
          >
            Room Rates
          </button>
        </div>
      </header>

      <div className="bg-white p-6 mt-2">
        <h1 className="text-xl font-bold text-center text-black">Modify Factors</h1>
        <p className="text-sm font-poppins font-semibold text-center mb-1 text-black">
          Adjust the pricing factors for the selected room type and template to tailor rates based
          on specific criteria.
        </p>
        <hr className="border border-gray-300 w-2/6 mx-auto mb-5" />

        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="w-full sm:w-1/3 relative">
            <input
              type="text"
              className="w-full px-4 py-2 text-md pl-10 bg-white text-black border border-gray-300 rounded-lg outline-none"
              placeholder="Search by Name or Percentage"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <FiSearch
              size={15}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleNewFactorOpen}
              className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
            >
              <FiPlus size={16} />
              Create New Factor
            </button>

            <button
              onClick={handleEditToggle}
              className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
            >
              {isEditing ? <FiSave size={16} /> : <FiEdit size={16} />}
              {isEditing ? 'Save Changes' : 'Edit Factors'}
            </button>
          </div>
        </div>

        {/* Factors Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredStats.map((stat) => (
            <div
              key={stat.id}
              className={`bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all relative ${
                !stat.isActive ? 'opacity-60' : ''
              }`}
            >
              {/* Header with status - Top Right */}
              <div className="flex justify-end items-start mb-1">
                {/* Status Display */}
                {isEditing ? (
                  <button
                    onClick={() => handleToggleActive(stat.id)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      editedStats.find((s) => s.id === stat.id)?.isActive
                        ? 'bg-black'
                        : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`block w-4 h-4 bg-white rounded-full transition-transform ${
                        editedStats.find((s) => s.id === stat.id)?.isActive
                          ? 'transform translate-x-7'
                          : 'transform translate-x-1'
                      }`}
                    />
                  </button>
                ) : (
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      stat.isActive
                        ? 'bg-gray-200 text-black border border-gray-300'
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}
                  >
                    {stat.isActive ? 'Active' : 'Inactive'}
                  </div>
                )}
              </div>

              {/* Factor Content */}
              <h3 className="text-black font-semibold mb-2 line-clamp-2 text-md">{stat.title}</h3>

              <div className="flex items-center justify-between">
                {isEditing ? (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDecrement(stat.id)}
                      className="px-3 py-1 text-black bg-gray-200 rounded-l-lg hover:bg-gray-300 transition-colors border border-gray-300"
                    >
                      -
                    </button>
                    <input
                      type="text"
                      className="w-20 px-2 py-1 text-black bg-white border border-gray-300 rounded text-center outline-none font-medium"
                      value={
                        editedStats.find((editedStat) => editedStat.id === stat.id)?.value ||
                        stat.value
                      }
                      onChange={(e) => handleValueChange(stat.id, e.target.value)}
                    />
                    <button
                      onClick={() => handleIncrement(stat.id)}
                      className="px-3 py-1 text-black bg-gray-200 rounded-r-lg hover:bg-gray-300 transition-colors border border-gray-300"
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <span className="text-2xl font-bold text-black">{stat.value}%</span>
                )}
              </div>

              {/* Delete Button - Bottom Right (Only in Edit Mode) */}
              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => handleDeleteFactor(stat)}
                    className="p-2 text-white bg-red-500  rounded transition-colors flex items-center gap-1 text-sm"
                  >
                    <FiTrash2 size={14} />
                    Delete Factor
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <ToastContainer className="mt-12" />
      </div>

      {/* Confirm Delete Modal */}
      {isConfirmDeleteOpen && factorToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 border border-gray-200">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Delete Factor</h2>
              <p className="text-gray-600 text-lg mb-2">
                Are you sure you want to delete this factor?
              </p>
              <p className="text-black font-medium text-lg mb-4">"{factorToDelete.title}"</p>
              <p className="text-gray-500 text-sm mb-6">This action cannot be undone.</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-md font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-md font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Edit Modal */}
      {isConfirmEditOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 border border-gray-200">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Save Changes</h2>
              <p className="text-gray-600 text-lg mb-6">
                Are you sure you want to save all changes to the factors?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-md font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-md font-medium"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* New Factor Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 border border-gray-200">
            {/* Header */}
            <div className="px-6 py-3">
              <h3 className="text-xl font-semibold text-black">Create New Factor</h3>
              <p className="text-gray-600 text-sm mt-1">Add a new pricing factor to the system</p>
            </div>

            {/* Form */}
            <div className="p-6 space-y-2">
              <div>
                <label className="block text-black text-lg font-medium mb-2">Factor Title</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md outline-none text-md"
                  placeholder="e.g., Season, Demand, Event"
                  value={newFactor.title}
                  onChange={(e) => setNewFactor({ ...newFactor, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-black text-lg font-medium mb-2">
                  Factor Value (%)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md outline-none text-md"
                  placeholder="e.g., 0.10"
                  value={newFactor.value}
                  onChange={(e) => setNewFactor({ ...newFactor, value: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <div>
                  <label className="block text-black text-lg font-medium mb-1">Factor Status</label>
                  <p className="text-gray-800 text-sm font-semibold">
                    {newFactor.isActive
                      ? 'Active - Used in calculations'
                      : 'Inactive - Not used in calculations'}
                  </p>
                </div>
                <button
                  onClick={handleNewFactorToggleActive}
                  className={`w-14 h-7 rounded-full transition-colors ${
                    newFactor.isActive ? 'bg-black' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`block w-5 h-5 bg-white rounded-full transition-transform ${
                      newFactor.isActive ? 'transform translate-x-8' : 'transform translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 ">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleNewFactorClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNewFactorSave}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-lg font-medium"
                >
                  Create Factor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModifyFactors;
